import { createFileRoute } from "@tanstack/react-router";
import { SOCIAL_PREVIEW_IMAGE, SOCIAL_PREVIEW_ALT } from "@/lib/social-meta";
import { EXPERIMENTS } from "@/lib/experiments";
import { useServerFn } from "@tanstack/react-start";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState, type FormEvent } from "react";

import { supabase } from "@/integrations/supabase/client";
import { RumDashboard } from "@/components/site/RumDashboard";
import { ShareCardCopyStudio } from "@/components/site/ShareCardCopyStudio";
import {
  listCspReports,
  type CspReportRow,
} from "@/lib/csp-reports.functions";

export const Route = createFileRoute("/admin")({
  head: () => ({
    meta: [
      { title: "Admin — CSP Violation Reports | Virginia Bariatric" },
      { name: "robots", content: "noindex, nofollow" },
      {
        name: "description",
        content: "Internal admin dashboard for Content Security Policy reports.",
      },
      ...(import.meta.env.SSR ? [
        { property: "og:title", content: "Admin — CSP Violation Reports | Virginia Bariatric" },
        { property: "og:description", content: "Internal admin dashboard for Content Security Policy reports." },
        { property: "og:type", content: "website" },
        { property: "og:url", content: "https://virginiabariatric.com/admin" },
        { name: "twitter:card", content: "summary" },
        { name: "twitter:title", content: "Admin — CSP Violation Reports | Virginia Bariatric" },
        { name: "twitter:description", content: "Internal admin dashboard for Content Security Policy reports." },
        { property: "og:image", content: SOCIAL_PREVIEW_IMAGE },
        { property: "og:image:alt", content: SOCIAL_PREVIEW_ALT },
        { name: "twitter:image", content: SOCIAL_PREVIEW_IMAGE },
        { name: "twitter:image:alt", content: SOCIAL_PREVIEW_ALT },
      ] : []),
    ],
    links: [{ rel: "canonical", href: "https://virginiabariatric.com/admin" }],
  }),
  component: AdminPage,
});

function AdminPage() {
  const [session, setSession] = useState<{ userId: string; email: string | null } | null>(
    null,
  );
  const [checkingSession, setCheckingSession] = useState(true);

  useEffect(() => {
    let active = true;
    supabase.auth.getSession().then(({ data }) => {
      if (!active) return;
      const s = data.session;
      setSession(s ? { userId: s.user.id, email: s.user.email ?? null } : null);
      setCheckingSession(false);
    });
    const { data: sub } = supabase.auth.onAuthStateChange((_evt, s) => {
      setSession(s ? { userId: s.user.id, email: s.user.email ?? null } : null);
    });
    return () => {
      active = false;
      sub.subscription.unsubscribe();
    };
  }, []);

  if (checkingSession) {
    return (
      <main className="mx-auto max-w-3xl px-4 py-12">
        <p className="text-muted-foreground">Loading…</p>
      </main>
    );
  }

  if (!session) return <SignInCard />;

  return <ReportsDashboard email={session.email} />;
}

function SignInCard() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    const { error: signInError } = await supabase.auth.signInWithPassword({
      email,
      password,
    });
    if (signInError) setError(signInError.message);
    setBusy(false);
  };

  return (
    <main className="mx-auto max-w-md px-4 py-12">
      <h1 className="text-2xl font-semibold text-brand-mid">Admin sign in</h1>
      <p className="mt-2 text-sm text-muted-foreground">
        Sign in with an admin account to view CSP violation reports.
      </p>
      <form onSubmit={onSubmit} className="mt-6 space-y-4">
        <div>
          <label htmlFor="admin-email" className="block text-sm font-medium">
            Email
          </label>
          <input
            id="admin-email"
            type="email"
            required
            autoComplete="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="mt-1 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
          />
        </div>
        <div>
          <label htmlFor="admin-password" className="block text-sm font-medium">
            Password
          </label>
          <input
            id="admin-password"
            type="password"
            required
            autoComplete="current-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="mt-1 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
          />
        </div>
        {error ? <p className="text-sm text-destructive">{error}</p> : null}
        <button
          type="submit"
          disabled={busy}
          className="w-full rounded-md bg-brand-mid px-4 py-2 text-sm font-medium text-white disabled:opacity-60"
        >
          {busy ? "Signing in…" : "Sign in"}
        </button>
      </form>
    </main>
  );
}

function ReportsDashboard({ email }: { email: string | null }) {
  const queryClient = useQueryClient();
  const fetchReports = useServerFn(listCspReports);

  const { data, isLoading, isError, error, isFetching } = useQuery<CspReportRow[]>({
    queryKey: ["csp-reports"],
    queryFn: () => fetchReports({ data: undefined as never }),
  });

  const signOut = useMutation({
    mutationFn: async () => {
      await supabase.auth.signOut();
      await queryClient.removeQueries({ queryKey: ["csp-reports"] });
    },
  });

  return (
    <main className="mx-auto max-w-6xl px-4 py-10">
      <header className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-brand-mid">
            CSP violation reports
          </h1>
          <p className="text-sm text-muted-foreground">
            Showing the most recent 200 reports.
            {email ? ` Signed in as ${email}.` : null}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => queryClient.invalidateQueries({ queryKey: ["csp-reports"] })}
            className="rounded-md border border-input px-3 py-1.5 text-sm"
            disabled={isFetching}
          >
            {isFetching ? "Refreshing…" : "Refresh"}
          </button>
          <button
            onClick={() => signOut.mutate()}
            className="rounded-md border border-input px-3 py-1.5 text-sm"
          >
            Sign out
          </button>
        </div>
      </header>

      <ShareCardCopyStudio />

      <ExperimentsSection />

      <RumDashboard />

      <section className="mt-8">
        {isLoading ? (
          <p className="text-sm text-muted-foreground">Loading reports…</p>
        ) : isError ? (
          <p className="text-sm text-destructive">
            {(error as Error)?.message ?? "Failed to load reports."}
          </p>
        ) : !data || data.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            No CSP violations recorded yet.
          </p>
        ) : (
          <div className="overflow-x-auto rounded-md border border-border">
            <table className="min-w-full divide-y divide-border text-sm">
              <thead className="bg-muted/50 text-left">
                <tr>
                  <th className="px-3 py-2 font-medium">Time</th>
                  <th className="px-3 py-2 font-medium">Blocked URL</th>
                  <th className="px-3 py-2 font-medium">Directive</th>
                  <th className="px-3 py-2 font-medium">Document</th>
                  <th className="px-3 py-2 font-medium">Source</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {data.map((r) => (
                  <tr key={r.id} className="align-top">
                    <td className="whitespace-nowrap px-3 py-2 text-muted-foreground">
                      {new Date(r.created_at).toLocaleString()}
                    </td>
                    <td className="break-all px-3 py-2 font-mono text-xs">
                      {r.blocked_uri ?? "—"}
                    </td>
                    <td className="px-3 py-2 font-mono text-xs">
                      {r.effective_directive ?? r.violated_directive ?? "—"}
                    </td>
                    <td className="break-all px-3 py-2 font-mono text-xs">
                      {r.document_uri ?? "—"}
                    </td>
                    <td className="break-all px-3 py-2 font-mono text-xs">
                      {r.source_file
                        ? `${r.source_file}${r.line_number ? `:${r.line_number}` : ""}`
                        : "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </main>
  );
}

function ExperimentsSection() {
  return (
    <section className="mt-8">
      <h2 className="text-lg font-semibold text-brand-mid">
        Running experiments
      </h2>
      <p className="text-sm text-muted-foreground">
        A/B tests currently live on the site. A weekly reminder email is sent
        every Monday morning.
      </p>
      {EXPERIMENTS.length === 0 ? (
        <p className="mt-3 text-sm text-muted-foreground">
          No experiments are currently running.
        </p>
      ) : (
        <ul className="mt-3 space-y-4">
          {EXPERIMENTS.map((exp) => {
            const days = Math.max(
              1,
              Math.round((Date.now() - Date.parse(exp.started)) / 86_400_000),
            );
            return (
              <li
                key={exp.id}
                className="rounded-md border border-border p-4 text-sm"
              >
                <div className="flex flex-wrap items-center gap-2">
                  <span className="font-medium">{exp.name}</span>
                  <span
                    className={`rounded-full px-2 py-0.5 text-xs font-medium ${
                      exp.status === "running"
                        ? "bg-green-100 text-green-800"
                        : "bg-muted text-muted-foreground"
                    }`}
                  >
                    {exp.status}
                  </span>
                </div>
                <p className="mt-1 text-muted-foreground">
                  Started {exp.started} ({days} days ago) · id:{" "}
                  <code className="font-mono text-xs">{exp.id}</code>
                </p>
                <p className="mt-1">
                  <span className="font-medium">Hypothesis:</span>{" "}
                  {exp.hypothesis}
                </p>
                <p className="mt-1">
                  <span className="font-medium">Variants:</span>{" "}
                  {exp.variants.map((v) => v.id).join(" vs. ")}
                </p>
                <p className="mt-1">
                  <span className="font-medium">Pages:</span>{" "}
                  {exp.pages.join(", ")}
                </p>
                <p className="mt-1">
                  <span className="font-medium">GA4 events:</span>{" "}
                  {exp.events.join(", ")}
                </p>
                <p className="mt-1 text-muted-foreground">{exp.forceHint}</p>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}
